import { isSuccess, isUnauthorized, type IAPIRequestConfig, type IHTTPResponse } from '@yimoka/shared';
import type { IWebHttpClient } from '@yimoka/web-http';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import type {
  IAuthAccountStaff,
  IAuthAccountStaffAuthReply,
  IAuthAccountTenantLoginParams,
} from '@/entities/auth-account.d';
import {
  clearAuthSession,
  clearTenantAuthSession,
  getStaffToken,
  getUserToken,
  setStaff,
  setStaffToken,
  setTenantStaffs,
} from '@/session/auth-session';

const RETRY_MARKER = '__qiyunTenantAuthRetried';
const RECOVERY_REGISTRY_KEY = '__qiyunTenantWebAuthRecoveryRegistry__';
const PLATFORM_TENANT_ID = '1000';
const EXCLUDED_PATHS = new Set([
  '/base/iam/portal/staff/userBind',
  '/base/iam/portal/loginByUser',
  '/base/iam/portal/staff/verifyInviteLink',
  '/base/iam/portal/logout',
]);

type APIExecutor = IWebHttpClient['apiExecutor'];
type RetryRequestConfig = InternalAxiosRequestConfig & {
  [RETRY_MARKER]?: boolean;
  tenantAuthRecovery?: 'skip';
};
type RecoveryEntry = {
  promise: Promise<string | undefined>;
  userToken: string;
};
type RecoveryRegistry = Map<string, RecoveryEntry>;

interface InstallTenantAuthRecoveryOptions {
  getPlatformExecutor: () => APIExecutor;
  tenantClient: AxiosInstance;
}

/** getRecoveryRegistry 返回当前页面共享的恢复表，使 Host 与 Web Remote 之间也能按租户单飞。 */
function getRecoveryRegistry(): RecoveryRegistry {
  const scope = globalThis as typeof globalThis & Record<string, unknown>;
  const existing = scope[RECOVERY_REGISTRY_KEY];
  if (existing instanceof Map) return existing as RecoveryRegistry;

  const registry: RecoveryRegistry = new Map();
  scope[RECOVERY_REGISTRY_KEY] = registry;
  return registry;
}

/** readHeader 兼容 AxiosHeaders 与普通对象，读取失败请求实际携带的认证上下文。 */
function readHeader(config: RetryRequestConfig, key: string): string {
  const { headers } = config;
  const fromAxiosHeaders = headers.get?.(key);
  if (typeof fromAxiosHeaders === 'string') return fromAxiosHeaders;

  const matched = Object.entries(headers).find(([header]) => header.toLowerCase() === key.toLowerCase());
  return typeof matched?.[1] === 'string' ? matched[1] : '';
}

/** writeAuthorization 覆盖旧 token，避免请求拦截器的“仅缺失时注入”保留失效凭证。 */
function writeAuthorization(config: RetryRequestConfig, token: string): void {
  const { headers } = config;
  if (headers.set) {
    headers.set('Authorization', token);
    return;
  }
  headers.Authorization = token;
}

/** normalizeRequestPath 把相对或绝对 API 地址统一为不含查询参数的服务路径。 */
function normalizeRequestPath(url?: string): string {
  if (!url) return '';
  try {
    const { pathname } = new URL(url, 'http://tenant-web.local');
    return pathname.startsWith('/api/') ? pathname.slice(4) : pathname;
  } catch {
    return url.split(/[?#]/, 1)[0];
  }
}

/** shouldRecoverRequest 只允许真实租户业务请求进入续期，认证与邀请接口绝不递归恢复。 */
function shouldRecoverRequest(config: RetryRequestConfig): boolean {
  if (config[RETRY_MARKER] || config.signal?.aborted || config.tenantAuthRecovery === 'skip') return false;
  const tenantID = readHeader(config, 'x-md-global-tenantID');
  if (!tenantID || tenantID === PLATFORM_TENANT_ID) return false;

  const path = normalizeRequestPath(config.url);
  if (path.startsWith('/base/user/portal/')) return false;
  return !EXCLUDED_PATHS.has(path);
}

/** executePlatformRequest 使用捕获的平台用户世代显式请求 SaaS 接口，避免租户请求头泄漏。 */
async function executePlatformRequest<R, P = unknown>(
  executor: APIExecutor,
  userToken: string,
  config: IAPIRequestConfig<P>,
): Promise<IHTTPResponse<R, P>> {
  return executor({
    ...config,
    headers: {
      ...config.headers,
      Authorization: userToken,
      'x-md-global-tenantID': PLATFORM_TENANT_ID,
    },
  }) as Promise<IHTTPResponse<R, P>>;
}

/** isTargetStaff 只选择目标租户内仍启用的员工身份，并兼容数字型 tenantID。 */
function isTargetStaff(staff: IAuthAccountStaff, tenantID: string): boolean {
  return staff.switch !== false
    && staff.tenant?.switch !== false
    && (String(staff.tenantID) === tenantID || staff.tenant?.id === tenantID);
}

/** recoverTenantAuthorization 按旧 Web 顺序刷新绑定关系并换取目标租户员工 token。 */
async function recoverTenantAuthorization(
  executor: APIExecutor,
  tenantID: string,
  userToken: string,
): Promise<string | undefined> {
  if (!userToken || getUserToken() !== userToken) return undefined;

  const staffsRes = await executePlatformRequest<IAuthAccountStaff[], never>(
    executor,
    userToken,
    { method: 'GET', url: '/base/iam/portal/staff/userBind' },
  );
  if (getUserToken() !== userToken) return undefined;
  if (isUnauthorized(staffsRes)) {
    clearAuthSession();
    return undefined;
  }
  if (!isSuccess(staffsRes) || !Array.isArray(staffsRes.data)) return undefined;

  setTenantStaffs(staffsRes.data);
  const staff = staffsRes.data.find(item => isTargetStaff(item, tenantID));
  if (!staff) {
    clearTenantAuthSession(tenantID);
    return undefined;
  }

  const loginRes = await executePlatformRequest<IAuthAccountStaffAuthReply, IAuthAccountTenantLoginParams>(
    executor,
    userToken,
    {
      data: { staffID: staff.id, tenantID },
      method: 'POST',
      url: '/base/iam/portal/loginByUser',
    },
  );
  if (getUserToken() !== userToken) return undefined;
  if (isUnauthorized(loginRes)) {
    clearAuthSession();
    return undefined;
  }
  const token = isSuccess(loginRes) ? loginRes.data?.token?.trim() : '';
  if (!token) {
    clearTenantAuthSession(tenantID);
    return undefined;
  }

  setStaffToken(token, tenantID);
  if (loginRes.data?.staff) setStaff(loginRes.data.staff, tenantID);
  return token;
}

/** recoverTenantAuthorizationOnce 在同一页面内按租户和平台用户世代合并并发恢复请求。 */
async function recoverTenantAuthorizationOnce(
  executor: APIExecutor,
  tenantID: string,
  userToken: string,
): Promise<string | undefined> {
  const registry = getRecoveryRegistry();
  const existing = registry.get(tenantID);
  if (existing?.userToken === userToken) return existing.promise;

  const entry: RecoveryEntry = {
    userToken,
    promise: recoverTenantAuthorization(executor, tenantID, userToken),
  };
  registry.set(tenantID, entry);
  try {
    return await entry.promise;
  } finally {
    // 新账号可能已为同一租户发起下一代恢复，旧请求不能删除新账号的单飞记录。
    if (registry.get(tenantID) === entry) registry.delete(tenantID);
  }
}

/**
 * installTenantAuthRecovery 为 tenant-web 自己的 Axios 实例补齐旧 Web 的 HTTP 401 静默恢复与单次重放。
 */
export function installTenantAuthRecovery({
  getPlatformExecutor,
  tenantClient,
}: InstallTenantAuthRecoveryOptions): void {
  const requestUserTokens = new WeakMap<RetryRequestConfig, string>();
  tenantClient.interceptors.request.use((config) => {
    requestUserTokens.set(config as RetryRequestConfig, getUserToken());
    return config;
  });

  tenantClient.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetryRequestConfig | undefined;
    if (error.response?.status !== 401 || !config || !shouldRecoverRequest(config)) {
      return Promise.reject(error);
    }

    const requestUserToken = requestUserTokens.get(config);
    // 迟到的旧账号 401 不能把原写请求重放到刚切换的新账号上。
    if (!requestUserToken || getUserToken() !== requestUserToken) return Promise.reject(error);

    config[RETRY_MARKER] = true;
    const tenantID = readHeader(config, 'x-md-global-tenantID');
    const failedToken = readHeader(config, 'Authorization');
    const currentToken = getStaffToken(tenantID);
    let nextToken = currentToken && currentToken !== failedToken ? currentToken : undefined;

    if (!nextToken) {
      nextToken = await recoverTenantAuthorizationOnce(getPlatformExecutor(), tenantID, requestUserToken);
    }
    if (!nextToken || config.signal?.aborted) return Promise.reject(error);

    writeAuthorization(config, nextToken);
    return tenantClient.request(config);
  });
}
